import { STORAGE_KEYS } from "../config/newsConfig";
import { getNewsSortTime } from "./newsSort";

const UNREAD_MODEL_VERSION = "3";
const FIRST_VISIT_UNREAD_WINDOW_MS = 24 * 60 * 60 * 1000;

let fallbackUnreadBoundary = "";

const LEGACY_UNREAD_STORAGE_KEYS = [
  "newsAppUnreadBaselineCountsV2",
  "newsAppUnreadSourceCutoffsV2",
  "newsAppUnreadReadIdsV2",
];

const isValidDateString = (value) => {
  if (!value) return false;

  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp);
};

const createFreshUnreadBoundary = () => {
  return new Date(
    Date.now() - FIRST_VISIT_UNREAD_WINDOW_MS
  ).toISOString();
};

/*
|-----------------------------------------------------------------------------
| Initialize the browser's unread model
|-----------------------------------------------------------------------------
|
| Model V3 definition:
|   - the first unread snapshot starts exactly 24 hours before initialization;
|   - that boundary is fixed and survives refreshes;
|   - Load More never changes the unread boundary or total;
|   - only news published on/after the fixed boundary can reduce unread counts.
|
| A version change intentionally starts a clean 24-hour unread snapshot. The
| general read-link history is preserved so Home and Focus still share the same
| visual read state, but abandoned unread-only counters/cutoffs are discarded.
|
*/
export const getOrCreateUnreadCountSince = () => {
  try {
    if (typeof localStorage === "undefined") {
      if (!fallbackUnreadBoundary) {
        fallbackUnreadBoundary = createFreshUnreadBoundary();
      }

      return fallbackUnreadBoundary;
    }

    const storedVersion = localStorage.getItem(
      STORAGE_KEYS.unreadModelVersion
    );

    const storedBoundary = localStorage.getItem(
      STORAGE_KEYS.unreadCountSince
    );

    if (
      storedVersion === UNREAD_MODEL_VERSION &&
      isValidDateString(storedBoundary)
    ) {
      return new Date(storedBoundary).toISOString();
    }

    const boundary = createFreshUnreadBoundary();

    localStorage.setItem(
      STORAGE_KEYS.unreadModelVersion,
      UNREAD_MODEL_VERSION
    );

    localStorage.setItem(
      STORAGE_KEYS.unreadCountSince,
      boundary
    );

    /*
    | Old source cutoffs and previous experimental unread maps belong to a
    | different counting model. Keeping them would immediately distort the new
    | 24-hour snapshot, so only unread-specific state is reset here.
    */
    localStorage.removeItem(STORAGE_KEYS.sourceReadCutoffs);
    localStorage.removeItem(STORAGE_KEYS.unreadReadItems);
    localStorage.removeItem(STORAGE_KEYS.unreadCommittedItems);
    localStorage.removeItem(STORAGE_KEYS.unreadReadAllBaselines);
    localStorage.removeItem(
      STORAGE_KEYS.unreadSuppressedBackfillSessions
    );

    LEGACY_UNREAD_STORAGE_KEYS.forEach((key) => {
      localStorage.removeItem(key);
    });

    return boundary;
  } catch {
    if (!fallbackUnreadBoundary) {
      fallbackUnreadBoundary = createFreshUnreadBoundary();
    }

    return fallbackUnreadBoundary;
  }
};

export const getUnreadBoundaryTimestamp = (unreadCountSince) => {
  const timestamp = Date.parse(unreadCountSince || "");
  return Number.isFinite(timestamp) ? timestamp : 0;
};

export const getUnreadEligiblePublishedTimestamp = (
  news,
  unreadCountSince
) => {
  const boundaryTimestamp = getUnreadBoundaryTimestamp(
    unreadCountSince
  );

  const publishedTimestamp = getNewsSortTime(news);

  if (
    !boundaryTimestamp ||
    !publishedTimestamp ||
    publishedTimestamp < boundaryTimestamp
  ) {
    return 0;
  }

  return publishedTimestamp;
};

export const isNewsUnreadEligible = (
  news,
  unreadCountSince
) => {
  return (
    getUnreadEligiblePublishedTimestamp(
      news,
      unreadCountSince
    ) > 0
  );
};
